import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { Vote } from '@filmsloth/shared';
import { gestureOwner, previewVote, voteForDrag, type GestureOwner } from './swipeGesture.js';

interface SwipeState {
  /** Current offset from the resting position, in pixels. */
  dx: number;
  dy: number;
  /** True while a finger or mouse button is down on the card. */
  dragging: boolean;
  /** The vote the drag would cast if released now. */
  intent: Vote | null;
}

const RESTING: SwipeState = { dx: 0, dy: 0, dragging: false, intent: null };

interface Origin {
  x: number;
  y: number;
  at: number;
  /**
   * Whether the page had anywhere to go upwards when the finger landed. Read
   * once, at the start: it decides who gets a downward drag, and that answer
   * must not change halfway through one.
   */
  pageCanScrollUp: boolean;
}

/** A page scrolled by a pixel or two is, for the purpose of a thumb, at the top. */
const AT_TOP_TOLERANCE_PX = 2;

/**
 * Makes an element draggable as a swipe card.
 *
 * Pointer events rather than touch events: one code path covers finger, mouse
 * and pen, and `setPointerCapture` keeps the drag alive when the finger
 * leaves the card, which is exactly what a decisive flick does.
 *
 * The one thing pointer events cannot do is stop the page from scrolling.
 * That is `touch-action`'s job, and `touch-action` cannot express "the card
 * gets downward, the page gets upward" — so the element also carries a native
 * `touchmove` listener (non-passive, because React's own is passive and
 * `preventDefault` there does nothing) that locks the axis on the first few
 * pixels and cancels the scroll when the swipe is ours.
 */
export function useSwipeable(onVote: (vote: Vote) => void, enabled = true) {
  const [swipe, setSwipe] = useState<SwipeState>(RESTING);
  const origin = useRef<Origin | null>(null);
  const owner = useRef<GestureOwner>('undecided');
  const node = useRef<HTMLDivElement | null>(null);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      // Let the interactive bits of the card — the trailer, its buttons — do
      // their own job instead of starting a drag.
      if (event.target instanceof Element && event.target.closest('button, a, iframe, input')) {
        return;
      }
      if (!enabled || event.button !== 0) return;
      origin.current = {
        x: event.clientX,
        y: event.clientY,
        at: performance.now(),
        pageCanScrollUp: window.scrollY > AT_TOP_TOLERANCE_PX,
      };
      owner.current = 'undecided';
      event.currentTarget.setPointerCapture(event.pointerId);
      setSwipe({ ...RESTING, dragging: true });
    },
    [enabled],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const start = origin.current;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    setSwipe({ dx, dy, dragging: true, intent: previewVote({ dx, dy }) });
  }, []);

  /** Drop the drag without answering: the card snaps back where it was. */
  const abandon = useCallback(() => {
    origin.current = null;
    owner.current = 'undecided';
    setSwipe(RESTING);
  }, []);

  /**
   * The axis lock. Runs before the browser has committed to scrolling, decides
   * once per gesture, and from then on either keeps every move away from the
   * page or stays out of the way entirely.
   */
  const onTouchMove = useCallback(
    (event: TouchEvent) => {
      const start = origin.current;
      const touch = event.touches[0];
      if (!start || !touch) return;

      if (owner.current === 'undecided') {
        owner.current = gestureOwner(
          { dx: touch.clientX - start.x, dy: touch.clientY - start.y },
          start.pageCanScrollUp,
        );
        // The page it is. Let go of the card rather than dragging it along
        // behind a scroll nobody asked us to join.
        if (owner.current === 'page') {
          abandon();
          return;
        }
      }

      if (owner.current === 'card' && event.cancelable) event.preventDefault();
    },
    [abandon],
  );

  /**
   * A callback ref rather than an effect reading `ref.current`: the deck only
   * exists while there is a card, so it leaves the document when the queue
   * runs out and comes back as a new element when it refills. An effect would
   * not run again for that — it would hold the listener on the element that
   * left, and the lock would be gone with no sign of it.
   */
  const deckRef = useCallback(
    (element: HTMLDivElement | null) => {
      node.current?.removeEventListener('touchmove', onTouchMove);
      node.current = element;
      element?.addEventListener('touchmove', onTouchMove, { passive: false });
    },
    [onTouchMove],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const start = origin.current;
      origin.current = null;
      owner.current = 'undecided';
      setSwipe(RESTING);
      if (!start) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      const vote = voteForDrag({
        dx: event.clientX - start.x,
        dy: event.clientY - start.y,
        elapsed: performance.now() - start.at,
      });
      if (vote) onVote(vote);
    },
    [onVote],
  );

  // A cancelled pointer is not a released one: the browser took the touch
  // away mid-drag, and casting a vote on the way out would answer for
  // somebody who never let go.
  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      abandon();
    },
    [abandon],
  );

  return {
    swipe,
    /** Belongs on the same element as `handlers`: the axis lock lives here. */
    deckRef,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
  };
}
